/**
 * Service Stripe - Gestion des paiements et checkout
 * 
 * Ce service gère les interactions avec Stripe via les Edge Functions Supabase.
 * Il permet de créer des sessions de checkout pour l'achat de crédits.
 * 
 * Requirements: 4.5
 */

import { supabase } from "@/integrations/supabase/client";

/**
 * Options pour créer une session de checkout Stripe
 */
export interface CreateCheckoutSessionOptions {
  type: 'credits' | 'branding_pro' | 'seats';
  organizationId: string;
  creditQuantity?: number;
  seatCount?: number;
  successUrl?: string;
  cancelUrl?: string;
}

/**
 * Réponse de la création d'une session de checkout
 */
export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

/**
 * Erreur lors de la création d'une session de checkout
 */
export class CheckoutError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'CheckoutError';
  }
}

/**
 * Crée une session de checkout Stripe pour l'achat de crédits
 * 
 * @param options - Options de la session de checkout
 * @returns URL de redirection vers Stripe Checkout
 * @throws {CheckoutError} Si la création de la session échoue
 */
export async function createCreditCheckoutSession(
  options: CreateCheckoutSessionOptions
): Promise<CheckoutSessionResponse> {
  try {
    // Valider les paramètres
    if (!options.organizationId) {
      throw new CheckoutError(
        'organization_id est requis',
        'MISSING_ORGANIZATION_ID'
      );
    }

    if (options.type === 'credits' && !options.creditQuantity) {
      throw new CheckoutError(
        'credit_quantity est requis pour l\'achat de crédits',
        'MISSING_CREDIT_QUANTITY'
      );
    }

    if (options.type === 'credits' && options.creditQuantity) {
      if (options.creditQuantity < 1 || options.creditQuantity > 10000) {
        throw new CheckoutError(
          'La quantité de crédits doit être entre 1 et 10 000',
          'INVALID_CREDIT_QUANTITY'
        );
      }
    }

    // Préparer les métadonnées pour Stripe
    const requestBody = {
      type: options.type,
      organization_id: options.organizationId,
      credit_quantity: options.creditQuantity,
      seat_count: options.seatCount,
      success_url: options.successUrl || `${window.location.origin}/dashboard`,
      cancel_url: options.cancelUrl || `${window.location.origin}/dashboard`,
    };

    console.warn('[StripeService] Creating checkout session:', {
      type: options.type,
      organizationId: options.organizationId,
      creditQuantity: options.creditQuantity,
    });

    // Appeler l'Edge Function pour créer la session Stripe
    const { data, error } = await supabase.functions.invoke<CheckoutSessionResponse>(
      'create-checkout-session',
      {
        body: requestBody,
      }
    );

    if (error) {
      console.error('[StripeService] Error creating checkout session:', error);
      throw new CheckoutError(
        'Erreur lors de la création de la session de paiement',
        'EDGE_FUNCTION_ERROR',
        error
      );
    }

    if (!data || !data.checkout_url) {
      console.error('[StripeService] Invalid response from Edge Function:', data);
      throw new CheckoutError(
        'Réponse invalide du serveur',
        'INVALID_RESPONSE',
        data
      );
    }

    console.warn('[StripeService] Checkout session created successfully:', {
      sessionId: data.session_id,
      checkoutUrl: data.checkout_url,
    });

    return data;
  } catch (error) {
    // Si c'est déjà une CheckoutError, la relancer
    if (error instanceof CheckoutError) {
      throw error;
    }

    // Sinon, créer une nouvelle CheckoutError
    console.error('[StripeService] Unexpected error:', error);
    throw new CheckoutError(
      error instanceof Error ? error.message : 'Erreur inconnue',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Redirige l'utilisateur vers Stripe Checkout
 * 
 * @param checkoutUrl - URL de la session Stripe Checkout
 */
export function redirectToCheckout(checkoutUrl: string): void {
  
  window.location.href = checkoutUrl;
}
